import { useEffect, useRef, useState, useCallback } from "react";
import QuestionCard from "./QuestionCard";
import QuizHeader from "./QuizHeader";
import clickSfx from "./click.mp3";
import QuizLayout from "./QuizLayout";
import { motion, AnimatePresence } from "framer-motion";
import useSound from "use-sound";
import "./css/quiz.css";
import CelebrationBomb from "./CelebrationBomb";
import { useDispatch, useSelector } from "react-redux";
import {
  getNextQuestionThunk,
  sendAnswerThunk,
} from "../../../store/quiz/quiz.thunks";
import Loader from "../../UI/Loader";
import QuizCompleted from "./QuizCompleted";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react/dist/iconify.js";
import NoInternetScreen from "../../UI/NoInternetScreen";

/* ═══════════════════════════════════════
   Hook: orientación reactiva
═══════════════════════════════════════ */
const useOrientation = () => {
  const getState = () => ({
    isLandscape: window.matchMedia("(orientation: landscape)").matches,
    isMobile: window.matchMedia("(max-width: 768px)").matches,
    isSmallHeight: window.matchMedia("(max-height: 500px)").matches,
  });
  const [state, setState] = useState(getState);
  useEffect(() => {
    const h = () => setState(getState());
    window.addEventListener("resize", h);
    window.addEventListener("orientationchange", h);
    return () => {
      window.removeEventListener("resize", h);
      window.removeEventListener("orientationchange", h);
    };
  }, []);
  return state;
};

/* ═══════════════════════════════════════
   ScaleButton — tooltip siempre visible en portrait
═══════════════════════════════════════ */
const ScaleButton = ({
  id,
  className,
  icon,
  label,
  disabled,
  onClick,
  isActive, // resaltado tras click
  onInteract,
  isPortrait,
  tooltipPosition = "center",
}) => {
  const handleClick = () => {
    if (!disabled) {
      onInteract?.(id);
      onClick();
    }
  };

  const activeClass = isActive ? "btn-selected" : "";

  return (
    <div className="scale-btn-wrapper">
      {/* Tooltip siempre visible en portrait */}
      {isPortrait && (
        <div className={`scale-label-tooltip tooltip-${tooltipPosition}`}>
          {label}
        </div>
      )}

      <button
        disabled={disabled}
        className={`scale-btn ${className} ${activeClass}${isPortrait ? " portrait-mode" : ""}`}
        onClick={handleClick}
        aria-label={label}
      >
        <Icon icon={icon} className="scale-btn-icon" aria-hidden="true" />
        {/* Texto visible solo fuera de portrait */}
        <span className="scale-btn-text">{label}</span>
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════
   Quiz — componente principal
═══════════════════════════════════════ */
const Quiz = () => {
  const dispatch = useDispatch();
  const { currentQuestion, progreso, sessionId, completed, loading } =
    useSelector((state) => state.quiz);

  const [playClick] = useSound(clickSfx, { volume: 0.25 });
  const [showCelebration, setShowCelebration] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [celebrationMilestone, setCelebrationMilestone] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [activeBtn, setActiveBtn] = useState(null); // btn resaltado tras click
  const lastCelebrated = useRef(0);

  const { isLandscape, isMobile, isSmallHeight } = useOrientation();
  const isCompactLandscape = isLandscape && isSmallHeight;
  const isPortrait = isMobile && !isLandscape;

  useEffect(() => {
    if (sessionId) dispatch(getNextQuestionThunk());
  }, [sessionId, dispatch]);

  useEffect(() => {
    const current = progreso.actual;
    const isMilestone = current > 0 && current % 10 === 0;
    if (
      isMilestone &&
      current !== lastCelebrated.current &&
      !completed &&
      !showCelebration
    ) {
      lastCelebrated.current = current;
      setCelebrationMilestone(current);
      setShowCelebration(true);
      setTimeout(() => {
        setShowCelebration(false);
        setCelebrationMilestone(null);
      }, 2500);
    }
  }, [progreso.actual, completed, showCelebration]);

  // Limpiar botón activo al cambiar pregunta
  useEffect(() => {
    setActiveBtn(null);
  }, [currentQuestion]);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (!currentQuestion && !completed) return null;

  const pregunta = currentQuestion.configuracion;

  const handleAnswer = async ({ side, respuestaId, isNeutral, btnId }) => {
    if (isAnswering || loading) return;
    setIsAnswering(true);
    setActiveBtn(btnId);
    playClick();

    const payload = {
      sesion_evaluacion: sessionId,
      configuracion_vs: pregunta.id,
      frase_seleccionada: isNeutral
        ? null
        : side === "izq"
          ? pregunta.frase_1_detalle.id
          : pregunta.frase_2_detalle.id,
      respuesta_seleccionada: respuestaId,
      orden_presentado: currentQuestion.orden_actual,
      tiempo_respuesta_segundos: 1,
    };

    const result = await dispatch(sendAnswerThunk(payload));
    if (result.type.endsWith("/rejected")) {
      toast.error(result.payload || "Error al enviar la respuesta", {
        position: "top-center",
        autoClose: 5000,
      });
      setIsAnswering(false);
      setActiveBtn(null);
      return;
    }
    if (!result.payload?.data?.evaluacion_completada)
      dispatch(getNextQuestionThunk());
    setIsAnswering(false);
  };

  if (completed) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <QuizCompleted />
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  if (!isOnline) {
    return <NoInternetScreen />;
  }

  const nonNeutral = currentQuestion.opciones_respuesta.filter(
    (op) => !op.is_neutral,
  );
  const neutralOption = currentQuestion.opciones_respuesta.find(
    (op) => op.is_neutral,
  );
  const totalId = nonNeutral[0]?.id;
  const ligeroId = nonNeutral[1]?.id;

  const btnProps = (id, tooltipPosition) => ({
    isActive: activeBtn === id,
    onInteract: setActiveBtn,
    isPortrait,
    tooltipPosition,
  });

  return (
    <QuizLayout>
      <div className="quiz-scope">
        <QuestionCard>
          <AnimatePresence>
            {showCelebration && (
              <CelebrationBomb
                message={`¡Pregunta ${celebrationMilestone}!`}
                intensity={50}
                duration={3}
                size="md"
              />
            )}
          </AnimatePresence>

          {loading && (
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text="Preparando tu siguiente pregunta..."
            />
          )}

          <QuizHeader
            actual={progreso.actual}
            total={progreso.total}
            progreso={progreso.porcentaje}
          />

          <h3 className="quiz-title">
            ¿Con cuál de estas afirmaciones te identificas más?
          </h3>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion.orden_actual}
              className="quiz-content"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.4 }}
            >
              <div className="quiz-cards">
                <div className="quiz-card quiz-card--left">
                  <div className="quiz-badge">
                    <Icon icon="ph:star-four-fill" />
                  </div>
                  <p>{pregunta.frase_1_detalle.frase}</p>
                </div>
                <div className="quiz-card quiz-card--right">
                  <div className="quiz-badge">
                    <Icon icon="ph:diamond-fill" />
                  </div>
                  <p>{pregunta.frase_2_detalle.frase}</p>
                </div>
              </div>

              {/* Divider — texto oculto en portrait mobile via CSS */}
              <div className="quiz-divider">
                <span className="quiz-divider-text">Selecciona tu nivel</span>
              </div>

              <div
                className={`quiz-scale-options${isCompactLandscape ? " compact-landscape" : ""}${isPortrait ? " portrait-layout" : ""}`}
              >
                <ScaleButton
                  id="left-strong"
                  className="left strong"
                  icon="mdi:arm-flex"
                  label={nonNeutral[0]?.frase}
                  disabled={isAnswering || loading}
                  onClick={() =>
                    handleAnswer({
                      side: "izq",
                      respuestaId: totalId,
                      btnId: "left-strong",
                    })
                  }
                  {...btnProps("left-strong", "left")}
                />
                <ScaleButton
                  id="left-soft"
                  className="left soft"
                  icon="mdi:thumb-up-outline"
                  label={nonNeutral[1]?.frase}
                  disabled={isAnswering || loading}
                  onClick={() =>
                    handleAnswer({
                      side: "izq",
                      respuestaId: ligeroId,
                      btnId: "left-soft",
                    })
                  }
                  {...btnProps("left-soft", "left")}
                />
                <ScaleButton
                  id="neutral"
                  className="neutral"
                  icon="mdi:scale-balance"
                  label={neutralOption?.frase}
                  disabled={isAnswering || loading}
                  onClick={() =>
                    handleAnswer({
                      isNeutral: true,
                      respuestaId: neutralOption?.id,
                      btnId: "neutral",
                    })
                  }
                  {...btnProps("neutral", "center")}
                />
                <ScaleButton
                  id="right-soft"
                  className="right soft"
                  icon="mdi:thumb-up-outline"
                  label={nonNeutral[1]?.frase}
                  disabled={isAnswering || loading}
                  onClick={() =>
                    handleAnswer({
                      side: "der",
                      respuestaId: ligeroId,
                      btnId: "right-soft",
                    })
                  }
                  {...btnProps("right-soft", "right")}
                />
                <ScaleButton
                  id="right-strong"
                  className="right strong"
                  icon="mdi:arm-flex"
                  label={nonNeutral[0]?.frase}
                  disabled={isAnswering || loading}
                  onClick={() =>
                    handleAnswer({
                      side: "der",
                      respuestaId: totalId,
                      btnId: "right-strong",
                    })
                  }
                  {...btnProps("right-strong", "right")}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </QuestionCard>
      </div>
    </QuizLayout>
  );
};

export default Quiz;
